import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AutosizeTextarea,
  type AutosizeTextAreaRef,
} from "@/components/ui/autosize-textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertCircle,
  ChevronRight,
  Loader,
  Send,
  RefreshCw,
} from "lucide-react";
import { useNotesStore } from "@/store/notesStore";
import { NoteItem } from "./NoteItem";
import { toast } from "sonner";
import { NoteAssetUpload } from "./NoteAssetUpload";
import type { Note, NoteEntityType, OutputAsset } from "@forgepoint/shared";

const DEFAULT_LOADING_STATE = { isLoading: false, error: null };
const DEFAULT_CREATE_LOADING_STATE = { isCreating: false, createError: null };

interface NotesSectionProps {
  entityType: NoteEntityType;
  entityId: string;
  isEditing?: boolean;
  compact?: boolean;
}

/**
 * Reusable collapsible section for displaying and managing notes on any entity.
 * Renders a list of notes with an add-note form, loading, empty, and error states.
 * Defaults to expanded. Hidden when isEditing is true.
 * @param entityType - The type of entity the notes belong to
 * @param entityId - The ID of the entity
 * @param isEditing - Whether the parent entity is in edit mode
 */
export const NotesSection: React.FC<NotesSectionProps> = ({
  entityType,
  entityId,
  isEditing = false,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [attachedAssets, setAttachedAssets] = useState<OutputAsset[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [recentlyCreatedId, setRecentlyCreatedId] = useState<string | null>(
    null,
  );
  const textareaRef = useRef<AutosizeTextAreaRef>(null);
  const entityKey = `${entityType}:${entityId}`;

  const fetchNotes = useNotesStore((s) => s.fetchNotes);
  const createNote = useNotesStore((s) => s.createNote);
  const updateNote = useNotesStore((s) => s.updateNote);
  const deleteNote = useNotesStore((s) => s.deleteNote);
  const notesMap = useNotesStore((s) => s.notes);
  const noteIds = useNotesStore((s) => s.notesByEntity.get(entityKey));
  const loadingState =
    useNotesStore((s) => s.loadingStates.get(entityKey)) ??
    DEFAULT_LOADING_STATE;
  const createLoadingState =
    useNotesStore((s) => s.createLoadingStates.get(entityKey)) ??
    DEFAULT_CREATE_LOADING_STATE;

  const notes = useMemo(() => {
    if (!noteIds) {
      return [];
    }
    return noteIds
      .map((id) => notesMap.get(id))
      .filter((note): note is Note => note !== undefined);
  }, [noteIds, notesMap]);

  useEffect(() => {
    fetchNotes(entityType, entityId);
  }, [entityType, entityId, fetchNotes]);

  useEffect(() => {
    if (!recentlyCreatedId) {
      return;
    }
    const timeout = setTimeout(() => setRecentlyCreatedId(null), 2000);
    return () => clearTimeout(timeout);
  }, [recentlyCreatedId]);

  if (isEditing) {
    return null;
  }

  /**
   * Handles submitting a new note.
   */
  const handleSubmit = async () => {
    const trimmed = newNoteContent.trim();
    if (!trimmed || isUploadingFile) {
      return;
    }

    const result = await createNote({
      entityType,
      entityId,
      content: trimmed,
      assetIds:
        attachedAssets.length > 0 ? attachedAssets.map((a) => a.id) : undefined,
    });

    if (result) {
      setNewNoteContent("");
      setAttachedAssets([]);
      setRecentlyCreatedId(result.id);
      toast.success("Note added");
      textareaRef.current?.textArea.focus();
    } else {
      toast.error("Failed to add note");
    }
  };

  /**
   * Handles updating a note's content and attachments.
   * @param noteId - The ID of the note to update
   * @param content - The new content
   * @param assetIds - Optional updated asset IDs
   */
  const handleUpdate = async (
    noteId: string,
    content: string,
    assetIds?: string[],
  ) => {
    const result = await updateNote(noteId, content, assetIds);
    if (result) {
      toast.success("Note updated");
    } else {
      toast.error("Failed to update note");
    }
  };

  /**
   * Handles deleting a note.
   * @param noteId - The ID of the note to delete
   */
  const handleDelete = async (noteId: string) => {
    const success = await deleteNote(noteId, entityType, entityId);
    if (success) {
      toast.success("Note deleted");
    } else {
      toast.error("Failed to delete note");
    }
  };

  /**
   * Handles keyboard shortcuts in the textarea.
   * @param e - The keyboard event
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const hasContent = newNoteContent.trim().length > 0;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={compact ? "pb-1" : "border-t pt-6 pb-6"}
    >
      {/* Header */}
      <CollapsibleTrigger
        className={`flex items-center gap-2 w-full group cursor-pointer select-none ${
          compact ? "mb-3" : "mb-4"
        }`}
      >
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
        />
        <h3
          className={
            compact ? "text-sm font-semibold" : "text-lg font-semibold"
          }
        >
          Notes
        </h3>
        {!loadingState.isLoading && notes.length > 0 && (
          <Badge variant="secondary" className="text-xs tabular-nums">
            {notes.length}
          </Badge>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        {/* Composer */}
        <div
          className={`
            flex flex-col gap-1.5 mb-4 rounded-lg border p-2
            transition-[border-color,box-shadow] duration-200 ease-out
            ${isFocused ? "border-primary/40 shadow-sm shadow-primary/5" : "border-border"}
          `}
        >
          <div className="flex gap-2">
            <AutosizeTextarea
              ref={textareaRef}
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Add a note..."
              minHeight={36}
              maxHeight={150}
              className="flex-1 text-sm border-0 shadow-none focus-visible:ring-0 resize-none p-0 px-1"
              disabled={createLoadingState.isCreating}
            />
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className={`
                    shrink-0 self-end h-8 w-8 rounded-md
                    transition-all duration-200
                    ${hasContent ? "opacity-100 text-primary hover:bg-primary/10 hover:-translate-y-0.5" : "opacity-40"}
                  `}
                  onClick={handleSubmit}
                  disabled={
                    !hasContent ||
                    createLoadingState.isCreating ||
                    isUploadingFile
                  }
                >
                  {createLoadingState.isCreating ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Send note
                <kbd className="ml-1.5 inline-flex items-center rounded border border-white/20 px-1.5 py-0.5 text-xs font-mono opacity-80">
                  {navigator.platform.includes("Mac")
                    ? "\u2318+\u23CE"
                    : "Ctrl+\u23CE"}
                </kbd>
              </TooltipContent>
            </Tooltip>
          </div>
          <NoteAssetUpload
            attachedAssets={attachedAssets}
            onAssetAdded={(asset) =>
              setAttachedAssets((prev) => [...prev, asset])
            }
            onAssetRemoved={(assetId) =>
              setAttachedAssets((prev) => prev.filter((a) => a.id !== assetId))
            }
            onUploadingChange={setIsUploadingFile}
            disabled={createLoadingState.isCreating}
          />
        </div>

        {/* Notes list */}
        {loadingState.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex gap-3 p-3 border rounded-lg"
                style={{ opacity: 1 - i * 0.25 }}
              >
                <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : loadingState.error ? (
          <div className="flex items-center justify-between gap-2 p-3 border border-destructive/20 rounded-lg bg-destructive/5 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {loadingState.error}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0 h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => fetchNotes(entityType, entityId)}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry
            </Button>
          </div>
        ) : notes.length === 0 ? null : (
          <div className="space-y-2">
            {notes.map((note, index) => (
              <div
                key={note.id}
                className="animate-in fade-in slide-in-from-bottom-1 duration-200 fill-mode-both"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <NoteItem
                  note={note}
                  onDelete={handleDelete}
                  onUpdate={handleUpdate}
                  isNew={note.id === recentlyCreatedId}
                />
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
